import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, CheckCircle, XCircle, ArrowUp, Flame, Shield, Info } from 'lucide-react';
import { useSlaAccess } from '../../utils/slaAccessControl';

/**
 * SLA Dashboard Access Guide Component
 * 
 * This component demonstrates how to properly access and display SLA information
 * based on user story requirements and role-based access control.
 * 
 * User Story Requirements:
 * - Death claims: 72 hours (3 days) maximum
 * - Disability claims: 120 hours (5 days) maximum  
 * - Critical Illness claims: 96 hours (4 days) maximum
 * - Role-based access: Claims Officer, Approver, Finance
 * - Visual indicators for deadline compliance
 * - Automatic escalation at 6h, 12h, and 24h post-breach
 */
const SlaAccessGuide = ({ user }) => {
  const [_badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const slaAccess = useSlaAccess(user);

  const fetchSlaInformation = React.useCallback(async () => {
    try {
      // Fetch SLA dashboard data if user has access
      // Fetch SLA dashboard data if user has access
      if (slaAccess.canAccessDashboard) {
        const badgeResponse = await fetch('/api/sla/dashboard-badges');

        if (badgeResponse.ok) {
          const badgeResult = await badgeResponse.json();
          setBadgeData(badgeResult);
        }
      }
    } catch (error) {
      console.error('Error fetching SLA information:', error);
    } finally {
      setLoading(false);
    }
  }, [slaAccess]);

  useEffect(() => {
    fetchSlaInformation();
  }, [fetchSlaInformation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading SLA information...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <Shield className="w-6 h-6 mr-2 text-blue-600" />
          SLA Dashboard Access Guide
        </h1>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-blue-800 mb-2">User Story Requirements</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Death claims: 72 hours (3 days) maximum processing time</li>
            <li>• Disability claims: 120 hours (5 days) maximum processing time</li>
            <li>• Critical Illness claims: 96 hours (4 days) maximum processing time</li>
            <li>• Role-based access: Claims Officer, Approver, Finance</li>
            <li>• Visual indicators for deadline compliance</li>
            <li>• Automatic escalation at 6h, 12h, and 24h post-breach</li>
          </ul>
        </div>

        {user && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Access Level</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span>Dashboard Access:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  slaAccess.canAccessDashboard ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {slaAccess.canAccessDashboard ? 'Granted' : 'Denied'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>SLA Badges:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  slaAccess.shouldShowBadges ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {slaAccess.shouldShowBadges ? 'Visible' : 'Hidden'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>SLA Metrics:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  slaAccess.canViewSlaMetrics ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {slaAccess.canViewSlaMetrics ? 'Available' : 'Restricted'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Manual SLA Check:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  slaAccess.canTriggerCheck ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {slaAccess.canTriggerCheck ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Access Instructions */}
        {!slaAccess.canAccessDashboard && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Info className="w-5 h-5 text-yellow-600 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Limited Access</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  {slaAccess.hasLimitedAccess ? 
                    'You can see SLA badges on your submitted claims, but dashboard access is restricted to Claims Officers, Approvers, Finance team, and Administrators.' :
                    'SLA dashboard access is restricted to Claims Officers, Approvers, Finance team, and Administrators. Contact your administrator for access.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Access Instructions */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Access SLA Features</h3>
          <div className="space-y-3 text-sm">
            <div>
              <strong>Dashboard Quick Actions:</strong> Look for the "SLA Monitoring" card on your dashboard's quick actions section.
            </div>
            <div>
              <strong>Direct Navigation:</strong> Type <code className="bg-gray-100 px-1 rounded">/sla-dashboard</code> in the address bar.
            </div>
            <div>
              <strong>Claims List:</strong> SLA badges appear in the SLA Status column of claims tables.
            </div>
            <div>
              <strong>Claim Details:</strong> Detailed SLA cards are shown in individual claim detail pages.
            </div>
          </div>

          {slaAccess.canAccessDashboard && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm text-green-800 font-medium">
                  You have access to all SLA monitoring features! 
                  <a href="/sla-dashboard" className="ml-1 underline hover:text-green-900">
                    Go to SLA Dashboard →
                  </a>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlaAccessGuide;
